import { Component, Inject, inject, signal, ViewChild } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { LoaderService } from '../../../core/service/loader.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { fetchPaginatedData, handlePaginatedResponse, registerSearchHandlers } from '../../../core/helpers/search-helper';
import { FileMangerService } from '../../../core/service/file-manger/file-manger.service';
import { forkJoin, map, Subject } from 'rxjs';
import { MatSelect } from '@angular/material/select';
import { createPaginatedState, PaginatedState, SearchBinding } from '../../../core/constants/filter-types';
import { FormControl } from '@angular/forms';
import { LocalStorageService } from '../../../core/service/localStorage/local-storage.service';
import { PopupServiceService } from '../../../core/service/popup/popup-service.service';

@Component({
  selector: 'app-switch-user',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './switch-user.component.html',
  styleUrl: './switch-user.component.scss'
})
export class SwitchUserComponent {

  // ============================= //
  //       DEPENDENCY INJECTION    //
  // ============================= //
  private readonly loader = inject(LoaderService);
  private readonly fileMangerService = inject(FileMangerService);
  private readonly localStorage = inject(LocalStorageService);
  public readonly toast = inject(PopupServiceService);

  constructor(
    public dialogRef: MatDialogRef<SwitchUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  // ============================================================
  // 🔹 Component State & Constants
  // ============================================================
  private destroy$ = new Subject<void>();
  private readonly PIXEL_TOLERANCE = 3.0;
  private isLoadingMoreMap = new Map<string, boolean>();
  isUserPanelOpen: boolean = false;
  limit = 100;
  currentUser: any;
  userMap = new Map<string, any>(); // Map userName to full user object to get ID

  // ============================================================
  // 🔹 Template References
  // ============================================================
  @ViewChild('selectUsers') selectUsers!: MatSelect;

  // ============================================================
  // 🔹 States
  // ============================================================
  userState: PaginatedState<any> = createPaginatedState();

  // ============================================================
  // 🔹 Signals
  // ============================================================
  selectedUserId = signal<string | undefined>(undefined);

  // ============================================================
  // 🔹 Form Controls
  // ============================================================
  userNameSearchControl = new FormControl('');


  /** Close dialog without saving */
  onClose(): void {
    this.dialogRef.close();
  }

  // ============================= //
  //           API CALL            //
  // ============================= //

  getAllUsers() {
    fetchPaginatedData({
      state: this.userState,
      limit: this.limit,
      fetchFn: () => this.fetchUsersData(),
    });
  };

  private fetchUsersData() {
    return this.fileMangerService.getUsersList({
      skip: this.userState.skip,
      limit: this.limit,
      urm: undefined,
      driveAccess: 'true',
      searchTerm: this.userState.searchTerm,
      optionIds: this.userState.selected,
    }).pipe(map((res: any) => {
      const mappedData =
        res?.data?.data
          ?.filter((v: any) => v?.userCredentials?.email !== this.currentUser?.email)
          ?.map((v: any) => {
            const mappedUser = {
              id: v?.id,
              name: `${v?.userDetails?.firstName} ${v?.userDetails?.lastName} (${v?.roles?.name})`,
              userName: v?.userCredentials?.userName,
              userEmail: v?.userCredentials?.email,
            };
            // Store user details in map for later retrieval
            this.userMap.set(mappedUser.userName, mappedUser);

            return mappedUser;
          }) ?? [];
      res.data.data = mappedData;
      return res;
    }));
  };

  private handleUsersResponse(res: any) {
    handlePaginatedResponse(this.userState, res, this.limit);
  }

  private setLoadingState(isLoading: boolean) {
    this.userState.loading = isLoading;
  }

  onUserChange(value: string){
    this.userState.selected = value;
  }


  // ============================================================
  // 🔹 Scroll Handling
  // ============================================================
  private hasScrolledToBottom(target: any): boolean {
    return (
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
      this.PIXEL_TOLERANCE
    );
  }

  registerPanelScrollEvent(select: MatSelect, type: 'users') {
    const panel = select?.panel?.nativeElement;
    if (!panel) return;

    panel.addEventListener('scroll', (event: any) => {
      if (this.hasScrolledToBottom(event.target) &&
        !this.isLoadingMoreMap.get(type)) {
        this.isLoadingMoreMap.set(type, true);
        if (type === 'users') this.getAllUsers();
        setTimeout(() => this.isLoadingMoreMap.set(type, false), 1000);
      }
    });
  }

  // ============================================================
  // 🔹 Lifecycle Hooks
  // ============================================================

  ngAfterViewInit(): void {
    this.selectUsers?.openedChange.subscribe((opened) => {
      if (this.userState.searchTerm) {
        this.userNameSearchControl.setValue(this.userState.searchTerm);
        return;
      }
      this.isUserPanelOpen = opened;
      if (opened) {
        setTimeout(
          () => this.registerPanelScrollEvent(this.selectUsers, 'users'),
          100
        );
      }
    });
  }

  ngOnInit(): void {
    this.loadInitialData();

    const bindings: SearchBinding<any>[] = [
      {
        control: this.userNameSearchControl,
        state: this.userState,
        fetchFn: () => this.getAllUsers(),
        isOpen: () => this.isUserPanelOpen
      },
    ];

    registerSearchHandlers(bindings, this.destroy$);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================
  // 🔹 Initial Data Loader
  // ============================================================

  private async loadInitialData() {
    this.setLoadingState(true);
    this.currentUser = await this.localStorage.getKeyIndexedDbKey('currentUser');
    forkJoin({
      users: this.fetchUsersData(),
    }).subscribe(
      (result: any) => {
        this.handleUsersResponse(result.users);
        this.setLoadingState(false);
      },
      (error: any) => {
        console.error('Error loading data:', error);
        this.setLoadingState(false);
      }
    );
  }

  // ============================================================
  // 🔹 Button Action
  // ============================================================
  onSwitch() {
    try{
      const selected = this.userState.data?.find((a) => a?.id == this.userState.selected);
      const payload = {
        crizac_user_id: selected.id,
        crizac_email: selected.userEmail,
        username: selected.userName
      }
      this.dialogRef.close({
        status: true,
        payload: payload,
      });

    } catch{
      this.toast.successAndErrorDialog('info', 'Please select user to switch.');
      return;
    }
  }

  onMyDrive() {
    this.dialogRef.close({status: false});
  }
}
